import { gql } from '@apollo/client';
import React from 'react';
import { ListingFinancingCalloutFields } from '../schemas/rql';
import { ListingAffirmCallout, ListingAffirmCalloutFragment } from './ListingAffirmCallout';
import { ListingKlarnaCallout, ListingKlarnaCalloutFragment } from './ListingKlarnaCallout';
import { ListingPayPalCallout, ListingPayPalCalloutFragment } from './ListingPayPalCallout';
import { useExpEnabled } from '@reverbdotcom/commons/src/user_hooks';
import experiments from '@reverbdotcom/commons/src/experiments';
import { MAX_PAY_LATER_CALLOUT_AMOUNT_CENTS } from './useExpressPayPal';

type Listing = ListingFinancingCalloutFields.Fragment;

interface Props {
  listing: Listing;
  showKlarnaCallout?: boolean;
  showPaypalCallout?: boolean;
}

export function ListingFinancingCallout({
  listing,
  showKlarnaCallout = false,
  showPaypalCallout = false,
}: Props) {
  const isPaypalCalloutAvailable = usePayPalCalloutAvailability(listing);

  if (listing.acceptedPaymentMethods.includes('klarna_account') && showKlarnaCallout) {
    return (
      <ListingKlarnaCallout
        listing={listing}
      />
    );
  }

  return (
    <>
      <ListingAffirmCallout
        listing={listing}
        simpleZeroPercentNudge
      />
      {isPaypalCalloutAvailable && showPaypalCallout &&
        <ListingPayPalCallout
          amountTotal={{
            currency: listing.currency,
            amount: listing.pricing.buyerPrice.amount,
            amountCents: listing.pricing.buyerPrice.amountCents,
          }}
        />
      }
    </>
  );
}

function usePayPalCalloutAvailability(listing: Listing) {
  return useExpEnabled(experiments.PAY_LATER_BUY_NOW) &&
    listing.acceptedPaymentMethods.includes('paypal_pay_later') &&
    listing.pricing.buyerPrice.amountCents <= MAX_PAY_LATER_CALLOUT_AMOUNT_CENTS;
}

export const ListingFinancingCalloutFragment = gql`
  fragment ListingFinancingCalloutFields on Listing {
    _id
    acceptedPaymentMethods
    pricing {
      buyerPrice {
        amountCents
      }
    }
    ...ListingAffirmCalloutFields
    ...ListingKlarnaCalloutFields
    ...ListingPayPalCalloutFields
  }
  ${ListingAffirmCalloutFragment}
  ${ListingKlarnaCalloutFragment}
  ${ListingPayPalCalloutFragment}
`;
